import { useState, useEffect, useMemo } from 'react';
import { teamsApi, usersApi, teamFoldersApi } from '@/lib/api';
import { formatDate } from '@/utils/dateFormatter';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import { eventEmitter, EVENTS } from '@/lib/events';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Users, Edit, Trash2, Search, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

interface Team {
  id: string;
  name: string;
  description: string;
  isAllMembers?: boolean; // When true, all subscription users (including future) are members
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
}

interface User {
  id: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  internalReferenceId: string; // Added - needed for team member creation
}

type SortField = 'name' | 'memberCount' | 'createdAt' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

export default function TeamsNew() {

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // Search and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useViewportPageSize({
    rowHeight: 54,      // Table row height (tuned to maximize rows without scrollbar)
    minPageSize: 5,
    maxPageSize: 50,
    bufferSpace: 45,    // 40px header row + 5px safety margin
    loading,            // Recalculate when loading completes
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isAllMembers: false, // When true, all subscription users (including future) are members
  });

  // Member selection
  const [users, setUsers] = useState<User[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [originalMembers, setOriginalMembers] = useState<string[]>([]); // Track original members for edit
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadTeams();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await usersApi.getSubscriptionUsers();
      console.log('[Teams] Loaded users from API:', response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await teamsApi.list();
      
      // Load member count for each team
      const teamsWithMembers = await Promise.all(
        response.data.map(async (team: Team) => {
          try {
            const membersResponse = await teamsApi.getMembers(team.id);
            // Add 1 to include the team owner (owner is tracked via Team.InternalReferenceId, not as a TeamMember)
            return { ...team, memberCount: membersResponse.data.length };
          } catch (error) {
            console.error(`[Teams] Failed to load members for team ${team.id}:`, error);
            // If error fetching members, still count the owner
            return { ...team, memberCount: 1 };
          }
        })
      );
      
      setTeams(teamsWithMembers);
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTeam(null);
    setFormData({ name: '', description: '', isAllMembers: false });
    setSelectedMembers([]);
    setOriginalMembers([]); // Clear original members
    setMemberSearchQuery('');
    setShowDialog(true);
  };

  const handleEdit = async (team: Team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description,
      isAllMembers: team.isAllMembers || false,
    });
    
    // Load team members and pre-select them
    try {
      const membersResponse = await teamsApi.getMembers(team.id);
      console.log('[Teams] Raw member response:', membersResponse.data);
      
      // Extract member InternalReferenceIds (backend uses camelCase serialization)
      const memberInternalRefIds = membersResponse.data
        .map((m: any) => {
          console.log('[Teams] Mapping member:', JSON.stringify(m));
          const id = m.internalReferenceId;
          console.log('[Teams] Extracted ID:', id);
          return id;
        })
        .filter((id: string) => id); // Filter out any empty values
      
      console.log('[Teams] Final extracted IDs:', memberInternalRefIds);
      console.log('[Teams] Current users list:', users.map(u => ({ 
        id: u.id, 
        internalReferenceId: u.internalReferenceId,
        displayName: u.displayName 
      })));
      
      setSelectedMembers(memberInternalRefIds);
      setOriginalMembers(memberInternalRefIds); // Store original for comparison
      console.log('[Teams] State updated - selectedMembers:', memberInternalRefIds);
    } catch (error) {
      console.error('Failed to load team members:', error);
      setSelectedMembers([]);
      setOriginalMembers([]);
    }
    
    setShowDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Team name is required');
      return;
    }

    try {
      if (editingTeam) {
        // Update team details
        await teamsApi.update(editingTeam.id, formData);
        
        // Handle member changes
        const membersToAdd = selectedMembers.filter(id => !originalMembers.includes(id));
        const membersToRemove = originalMembers.filter(id => !selectedMembers.includes(id));
        
        console.log('[Teams] Original members:', originalMembers);
        console.log('[Teams] Selected members:', selectedMembers);
        console.log('[Teams] Members to add:', membersToAdd);
        console.log('[Teams] Members to remove:', membersToRemove);
        
        // Add new members
        if (membersToAdd.length > 0) {
          try {
            await Promise.all(
              membersToAdd.map(internalReferenceId =>
                teamsApi.addMember(editingTeam.id, { 
                  internalReferenceId,
                  role: 0 // TeamRole.Member
                })
              )
            );
            console.log('[Teams] Added', membersToAdd.length, 'new members');
          } catch (error) {
            console.error('Failed to add some members:', error);
          }
        }
        
        // Remove members
        if (membersToRemove.length > 0) {
          try {
            console.log('[Teams] About to remove members:', membersToRemove);
            await Promise.all(
              membersToRemove.map(memberInternalReferenceId => {
                console.log('[Teams] Calling removeMember API - TeamId:', editingTeam.id, 'MemberInternalRefId:', memberInternalReferenceId);
                return teamsApi.removeMember(editingTeam.id, memberInternalReferenceId);
              })
            );
            console.log('[Teams] Removed', membersToRemove.length, 'members');
          } catch (error) {
            console.error('Failed to remove some members:', error);
          }
        }
        
        toast.success('Team updated successfully');
        eventEmitter.emit(EVENTS.TEAMS_UPDATED);
      } else {
        console.log('[Teams] Creating team with:', {
          formData,
          selectedMembers,
          selectedMemberCount: selectedMembers.length
        });
        
        const response = await teamsApi.create(formData);
        const newTeamId = response.data.id;
        
        console.log('[Teams] Team created:', response.data);
        
        // Add selected members to the team
        if (selectedMembers.length > 0) {
          try {
            console.log('[Teams] Adding members:', selectedMembers);
            const addResults = await Promise.all(
              selectedMembers.map(internalReferenceId => {
                console.log('[Teams] Adding member with ID:', internalReferenceId);
                return teamsApi.addMember(newTeamId, { 
                  internalReferenceId,
                  role: 0 // TeamRole.Member
                });
              })
            );
            console.log('[Teams] Add members results:', addResults);
            toast.success(`Team created with ${selectedMembers.length} member${selectedMembers.length !== 1 ? 's' : ''}`);
          } catch (memberError) {
            console.error('Failed to add some members:', memberError);
            toast.warning('Team created but some members could not be added');
          }
        } else {
          console.log('[Teams] No members to add, team created with owner only');
          toast.success('Team created successfully');
        }
        console.log('[Teams] Emitting TEAM_CREATED event');
        eventEmitter.emit(EVENTS.TEAM_CREATED);
        console.log('[Teams] TEAM_CREATED event emitted');
      }
      
      setShowDialog(false);
      loadTeams();
    } catch (error) {
      console.error('Failed to save team:', error);
      toast.error(`Failed to ${editingTeam ? 'update' : 'create'} team`);
    }
  };

  const handleDeleteClick = async (team: Team) => {
    // Check if team has any workflows or knowledge base shares
    try {
      const response = await teamFoldersApi.getStructure(team.id);
      const folders = response.data;

      // Calculate total workflow count across all folders
      const totalWorkflowCount = folders.reduce((sum: number, folder: any) => sum + (folder.workflowCount || 0), 0);

      if (totalWorkflowCount > 0) {
        toast.error(`Cannot delete team "${team.name}" because it contains ${totalWorkflowCount} workflow${totalWorkflowCount > 1 ? 's' : ''}. Please move or delete all workflows first.`);
        return;
      }

      // Check if team has any knowledge base collection shares
      const kbResponse = await teamsApi.hasKbShares(team.id);
      if (kbResponse.data.hasShares) {
        toast.error(`Cannot delete team "${team.name}" because it has shared knowledge base collections. Please remove the team from all knowledge base shares first.`);
        return;
      }

      setTeamToDelete({ id: team.id, name: team.name });
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error('Failed to check team dependencies:', error);
      toast.error('Failed to check team dependencies');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!teamToDelete) return;

    try {
      await teamsApi.delete(teamToDelete.id);
      toast.success('Team deleted successfully');
      eventEmitter.emit(EVENTS.TEAM_DELETED);
      setDeleteDialogOpen(false);
      setTeamToDelete(null);
      loadTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast.error('Failed to delete team');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Filter and sort teams
  const filteredAndSortedTeams = useMemo(() => teams
    .filter(team =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'memberCount') {
        aValue = a.memberCount || 0;
        bValue = b.memberCount || 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }), [teams, searchQuery, sortField, sortDirection]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  // Paginated teams
  const paginatedTeams = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedTeams.slice(startIndex, endIndex);
  }, [filteredAndSortedTeams, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedTeams.length / pageSize));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 md:p-6">
        {/* Header */}
        <div className="flex-none flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">Teams</h1>
            <p className="text-muted-foreground mt-1">
              Manage your teams and collaborate on workflows
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Team
          </Button>
        </div>

        {/* Search Bar */}
        {teams.length > 0 && (
          <div className="flex-none mb-4">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        )}

        {/* Table */}
        {teams.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No teams yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first team to collaborate on workflows
            </p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          </div>
        ) : filteredAndSortedTeams.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No teams found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search
            </p>
            <Button variant="outline" onClick={() => setSearchQuery('')}>
              <X className="h-4 w-4 mr-2" />
              Clear Search
            </Button>
          </div>
        ) : (
          <>
          <div data-table-container className="flex-1 overflow-auto">
            <div className="border rounded-lg">
            <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Name
                  {getSortIcon('name')}
                </button>
              </TableHead>
              <TableHead>Description</TableHead>
              <TableHead>
                <button
                  onClick={() => handleSort('memberCount')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Members
                  {getSortIcon('memberCount')}
                </button>
              </TableHead>
              <TableHead>
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Created
                  {getSortIcon('createdAt')}
                </button>
              </TableHead>
              <TableHead>
                <button
                  onClick={() => handleSort('updatedAt')}
                  className="flex items-center gap-2 hover:text-foreground"
                >
                  Updated
                  {getSortIcon('updatedAt')}
                </button>
              </TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedTeams.map((team) => (
                <TableRow
                  key={team.id}
                  className="hover:bg-muted/50"
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      {team.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-md truncate">
                    {team.description || '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span>{team.memberCount || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(team.createdAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(
                      new Date(team.updatedAt) < new Date(team.createdAt)
                        ? team.createdAt
                        : team.updatedAt
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(team)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(team)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
            </div>
          </div>
          {/* Pagination Footer */}
          <div data-pagination-footer className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredAndSortedTeams.length)} of {filteredAndSortedTeams.length} teams
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm font-medium whitespace-nowrap">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
          </>
        )}

        {/* Create/Edit Dialog */}
        {!loading && <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTeam ? 'Edit Team' : 'Create New Team'}</DialogTitle>
            <DialogDescription>
              {editingTeam ? 'Update team details' : 'Create a new team to collaborate on workflows'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Name *
                </label>
                <Input
                  id="name"
                  placeholder="e.g., Finance Team"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  Description
                </label>
                <Textarea
                  id="description"
                  placeholder="What is this team for?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Member Selection - Show for both create and edit */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Team Members</label>
                  {/* All Users Toggle - Compact inline */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="all-members" className="text-xs text-muted-foreground cursor-pointer">
                      All users (incl. future)
                    </label>
                    <Switch
                      id="all-members"
                      checked={formData.isAllMembers}
                      onCheckedChange={(checked) => setFormData({ ...formData, isAllMembers: checked })}
                    />
                  </div>
                </div>

                {/* Individual member selection - disabled when All Users is enabled */}
                <div className={formData.isAllMembers ? 'opacity-50 pointer-events-none' : ''}>
                  {/* Search */}
                  <div className="relative mb-2">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>

                {/* User Lists - Side by Side */}
                {loadingUsers ? (
                  <div className="flex items-center justify-center h-[250px] border rounded-md">
                    <div className="text-sm text-muted-foreground">Loading users...</div>
                  </div>
                ) : (() => {
                  const filteredUsers = users.filter(user =>
                    user.displayName?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                    user.email?.toLowerCase().includes(memberSearchQuery.toLowerCase())
                  );

                  // Split users into current members and available users
                  const currentMembers = filteredUsers.filter(user => selectedMembers.includes(user.internalReferenceId));
                  const availableUsers = filteredUsers.filter(user => !selectedMembers.includes(user.internalReferenceId));

                  const toggleMember = (userId: string) => {
                    setSelectedMembers(prev =>
                      prev.includes(userId)
                        ? prev.filter(id => id !== userId)
                        : [...prev, userId]
                    );
                  };

                  const selectAll = () => {
                    const allUserIds = users.map(u => u.internalReferenceId);
                    setSelectedMembers(allUserIds);
                  };

                  const deselectAll = () => {
                    setSelectedMembers([]);
                  };

                  return filteredUsers.length === 0 ? (
                    <div className="h-[180px] border rounded-md flex items-center justify-center">
                      <div className="text-center text-sm text-muted-foreground">
                        {memberSearchQuery ? 'No users found matching your search' : 'No users available'}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {/* Current Members Section */}
                      <div className="border rounded-md">
                        <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Current Members ({currentMembers.length})
                          </span>
                          {currentMembers.length > 0 && (
                            <button
                              type="button"
                              onClick={deselectAll}
                              className="text-xs text-primary hover:underline"
                            >
                              Deselect All
                            </button>
                          )}
                        </div>
                        <ScrollArea className="h-[180px]">
                          {currentMembers.length === 0 ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              No members added yet
                            </div>
                          ) : (
                            <div className="p-2 space-y-1">
                              {currentMembers.map((user) => (
                                <div
                                  key={user.internalReferenceId}
                                  className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                                  onClick={() => toggleMember(user.internalReferenceId)}
                                >
                                  <Checkbox
                                    checked={selectedMembers.includes(user.internalReferenceId)}
                                    onCheckedChange={() => toggleMember(user.internalReferenceId)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{user.displayName}</div>
                                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </div>

                      {/* Available Users Section */}
                      <div className="border rounded-md">
                        <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Available Users ({availableUsers.length})
                          </span>
                          {availableUsers.length > 0 && (
                            <button
                              type="button"
                              onClick={selectAll}
                              className="text-xs text-primary hover:underline"
                            >
                              Select All
                            </button>
                          )}
                        </div>
                        <ScrollArea className="h-[180px]">
                          {availableUsers.length === 0 ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              {memberSearchQuery ? 'No matches' : 'All users are members'}
                            </div>
                          ) : (
                            <div className="p-2 space-y-1">
                              {availableUsers.map((user) => (
                                <div
                                  key={user.internalReferenceId}
                                  className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                                  onClick={() => toggleMember(user.internalReferenceId)}
                                >
                                  <Checkbox
                                    checked={selectedMembers.includes(user.internalReferenceId)}
                                    onCheckedChange={() => toggleMember(user.internalReferenceId)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{user.displayName}</div>
                                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </div>
                    </div>
                  );
                })()}

                  {/* Selected Count */}
                  {!formData.isAllMembers && (() => {
                    const visibleSelectedCount = selectedMembers.filter(id =>
                      users.some(user => user.internalReferenceId === id)
                    ).length;
                    return visibleSelectedCount > 0 && (
                      <div className="text-xs text-muted-foreground mt-2">
                        {visibleSelectedCount} member{visibleSelectedCount !== 1 ? 's' : ''} selected
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingTeam ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{teamToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialog>
    </div>
  );
}
